import { createI18n } from 'vue-i18n';
import { LOCALE_CODES } from '@/config/localeOptions';
import {
  resolveInitialLanguage,
  getAcceptLanguageTag,
  persistAcceptLanguage,
} from '@/utils/app-language';
import { applyAcceptLanguageHeader } from '@/utils/request';

export const SUPPORTED_LOCALES = LOCALE_CODES;
export const FALLBACK_LOCALE = 'en';

const localeLoaders = {
  en: () => import('./en.json'),
  ar: () => import('./ar.json'),
  bn: () => import('./bn.json'),
  de: () => import('./de.json'),
  es: () => import('./es.json'),
  fr: () => import('./fr.json'),
  hi: () => import('./hi.json'),
  id: () => import('./id.json'),
  it: () => import('./it.json'),
  ja: () => import('./ja.json'),
  ko: () => import('./ko.json'),
  pl: () => import('./pl.json'),
  pt: () => import('./pt.json'),
  ru: () => import('./ru.json'),
  th: () => import('./th.json'),
  tl: () => import('./tl.json'),
  uk: () => import('./uk.json'),
  vi: () => import('./vi.json'),
  zh: () => import('./zh.json'),
  'zh-TW': () => import('./zh-TW.json'),
};

const loadedLocales = new Set();

const initialLanguage = resolveInitialLanguage();

const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: initialLanguage.i18nCode,
  fallbackLocale: FALLBACK_LOCALE,
  messages: {},
});

/** 按需加载语言包并注册到 i18n（无独立语言包时回退 en 文案） */
export async function loadLocaleMessages(locale) {
  const code = String(locale ?? '').trim();
  if (!code || loadedLocales.has(code)) return;
  const loader = localeLoaders[code];
  if (loader) {
    const mod = await loader();
    i18n.global.setLocaleMessage(code, mod.default ?? mod);
  } else {
    await loadLocaleMessages(FALLBACK_LOCALE);
    i18n.global.setLocaleMessage(code, i18n.global.getLocaleMessage(FALLBACK_LOCALE));
  }
  loadedLocales.add(code);
}

/** 启动时加载当前语言 + fallback，再挂载应用 */
export async function setupI18n() {
  const { i18nCode, acceptTag } = resolveInitialLanguage();
  await loadLocaleMessages(FALLBACK_LOCALE);
  if (i18nCode !== FALLBACK_LOCALE) {
    await loadLocaleMessages(i18nCode);
  }
  i18n.global.locale.value = i18nCode;
  applyAcceptLanguageHeader(acceptTag);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = acceptTag || i18nCode;
    document.documentElement.dir = i18nCode === 'ar' ? 'rtl' : 'ltr';
  }
  return i18n;
}

/**
 * 切换语言：持久化用户偏好、更新 Accept-Language 并刷新页面，
 * 以便重新请求接口与 keep-alive 缓存内容。
 * 仅此处写入 localStorage；未手动切换时始终跟随系统语言。
 */
export async function switchAppLocale(code) {
  const normalized = String(code ?? '').trim();
  if (!normalized) return;
  try {
    const { trackAnalyticsLog } = await import('@/utils/analytics-track');
    trackAnalyticsLog({
      key: 'locale.switch',
      label: '切换语言',
      value: { locale: normalized },
    });
  } catch {
    /* ignore */
  }
  await loadLocaleMessages(normalized);
  const acceptTag = getAcceptLanguageTag(normalized);
  persistAcceptLanguage(acceptTag);
  applyAcceptLanguageHeader(acceptTag);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = acceptTag || normalized;
    document.documentElement.dir = normalized === 'ar' ? 'rtl' : 'ltr';
  }
  // 刷新前尽量切 Tawk Widget；刷新后也会按 appLangUser 重新 getChatId
  if (typeof window !== 'undefined' && typeof window.dsTawkApplyLocale === 'function') {
    try {
      window.dsTawkApplyLocale(acceptTag || normalized);
    } catch {
      /* ignore */
    }
  }
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
}

export default i18n;
